import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDM } from '@/context/DMContext';
import { useToast } from '@/context/ToastContext';
import { acknowledgeOutreach, claimOutreach, releaseOutreachClaim } from '@/services/outreach';
import { claimHeldByOther, needsAcknowledgement } from '@/utils/outreach';
import type { OutreachConfig, OutreachRecord } from '@/types/outreach';
import { SayHelloSheet } from './SayHelloSheet';
import { ClaimedNoticeDialog } from './ClaimedNoticeDialog';
import { OutreachAcknowledgementDialog } from './OutreachAcknowledgementDialog';

interface FlowInput {
  leader: { uid: string; displayName: string; outreachAckVersion?: number } | null;
  config: OutreachConfig;
  waiting: OutreachRecord[];
}

/**
 * Everything that happens after "Say hello": acknowledgement (first time), claiming,
 * the compose sheet, the "someone is writing" notice. Lives outside the tray so the
 * sheet stays open when the tray closes.
 */
export function useNewMembersFlow({ leader, config, waiting }: FlowInput): {
  startHello: (record: OutreachRecord) => void;
  showClaimed: (record: OutreachRecord, heldByName: string) => void;
  busyId: string | null;
  element: ReactNode;
} {
  const navigate = useNavigate();
  const { openConversation } = useDM();
  const { showToast } = useToast();

  const [busyId, setBusyId] = useState<string | null>(null);
  const [active, setActive] = useState<OutreachRecord | null>(null);
  const [notice, setNotice] = useState<{
    record: OutreachRecord;
    by: string;
    minutes?: number;
  } | null>(null);
  const [ackFor, setAckFor] = useState<OutreachRecord | null>(null);
  // Accepted this session: the profile snapshot may lag behind the function's write.
  const [ackedVersion, setAckedVersion] = useState(0);
  const activeRef = useRef<OutreachRecord | null>(null);
  activeRef.current = active;

  const claimAndOpen = useCallback(
    async (record: OutreachRecord) => {
      setBusyId(record.memberId);
      try {
        const res = await claimOutreach(record.memberId);
        if (res.ok) {
          setActive(record);
        } else {
          setNotice({
            record,
            by: res.heldByName,
            minutes: undefined,
          });
        }
      } catch (e) {
        console.error('Failed to start hello:', e);
        showToast({
          message: 'Someone may have just welcomed them. The list will update.',
          type: 'info',
          duration: 3000,
        });
      } finally {
        setBusyId(null);
      }
    },
    [showToast]
  );

  const startHello = useCallback(
    (record: OutreachRecord) => {
      const acked = Math.max(leader?.outreachAckVersion ?? 0, ackedVersion);
      if (needsAcknowledgement({ outreachAckVersion: acked }, config)) {
        setAckFor(record);
        return;
      }
      void claimAndOpen(record);
    },
    [leader?.outreachAckVersion, ackedVersion, config, claimAndOpen]
  );

  const showClaimed = useCallback((record: OutreachRecord, heldByName: string) => {
    const startedMs = record.claim?.claimedAt?.toMillis?.();
    setNotice({
      record,
      by: heldByName,
      minutes: startedMs ? Math.max(0, Math.round((Date.now() - startedMs) / 60000)) : undefined,
    });
  }, []);

  const next = useMemo(() => {
    if (!active || !leader) return null;
    const now = Date.now();
    return (
      waiting.find(
        (r) => r.memberId !== active.memberId && !claimHeldByOther(r, leader.uid, now)
      ) ?? null
    );
  }, [active, waiting, leader]);
  const waitingAfter = active ? waiting.filter((r) => r.memberId !== active.memberId).length : 0;

  const cancelSheet = useCallback(() => {
    const current = activeRef.current;
    setActive(null);
    if (current) releaseOutreachClaim(current.memberId).catch(() => undefined);
  }, []);

  const element = (
    <>
      {ackFor && (
        <OutreachAcknowledgementDialog
          text={config.acknowledgement.text}
          onCancel={() => setAckFor(null)}
          onAccept={async () => {
            await acknowledgeOutreach();
            setAckedVersion(config.acknowledgement.version);
            const record = ackFor;
            setAckFor(null);
            void claimAndOpen(record);
          }}
        />
      )}
      {notice && (
        <ClaimedNoticeDialog
          memberName={notice.record.displayName}
          leaderName={notice.by}
          startedMinutesAgo={notice.minutes}
          onClose={() => setNotice(null)}
        />
      )}
      {active && leader && (
        <SayHelloSheet
          key={active.memberId}
          record={active}
          leader={leader}
          config={config}
          next={next}
          waitingAfter={waitingAfter}
          onCancel={cancelSheet}
          onDone={() => setActive(null)}
          onSayHelloToNext={(r) => {
            setActive(null);
            startHello(r);
          }}
          onOpenConversation={(conversationId) => {
            setActive(null);
            openConversation(conversationId);
            navigate('/chat/messages');
          }}
        />
      )}
    </>
  );

  return { startHello, showClaimed, busyId, element };
}
